// Pantalla principal del profesor: calendario de sus clases del mes + crear grupal.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { Spinner } from '@/components/atoms/Spinner';
import { Calendar, type DayDecorator } from '@/components/molecules/Calendar';
import { Header } from '@/components/organisms/Header';
import { CreateGroupDrawer } from '@/components/organisms/CreateGroupDrawer';
import { TeacherDayDialog } from '@/components/organisms/TeacherDayDialog';
import { useAuth } from '@/context/AuthContext';
import { ApiError } from '@/services/api';
import { listTeacherBookings, type BookingListItem } from '@/services/bookings';
import styles from './ReservasProfesor.module.scss';

function isoDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function ReservasProfesor() {
  const { state } = useAuth();
  const teacherId = state.status === 'authenticated' ? state.user.id : 0;

  const [month] = useState(() => new Date());
  const [bookings, setBookings] = useState<BookingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pickedDay, setPickedDay] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await listTeacherBookings(month.getFullYear(), month.getMonth() + 1);
      setBookings(r.items);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { void reload(); }, [reload]);

  const decorators = useMemo(() => {
    const map: Record<string, DayDecorator> = {};
    for (const b of bookings) {
      if (b.status !== 'scheduled') continue;
      const day = isoDate(new Date(b.scheduled_at));
      const acc = map[day] ?? {};
      if (b.type === 'one') acc.individual = (acc.individual ?? 0) + 1;
      else acc.group = (acc.group ?? 0) + 1;
      map[day] = acc;
    }
    return map;
  }, [bookings]);

  const dayBookings = useMemo(() => {
    if (!pickedDay) return [];
    return bookings.filter((b) => isoDate(new Date(b.scheduled_at)) === pickedDay);
  }, [bookings, pickedDay]);

  const totalScheduled = bookings.filter((b) => b.status === 'scheduled').length;

  return (
    <>
      <Header
        title="Mis clases"
        actions={
          <Button variant="primary" onClick={() => setDrawerOpen(true)}>
            <Icon name="plus" size={16} /> Programar grupal
          </Button>
        }
      />

      <main className={styles.main}>
        {loading ? (
          <div className={styles.center}><Spinner size="lg" /></div>
        ) : (
          <>
            <div className={styles.summary}>
              <span><strong>{totalScheduled}</strong> {totalScheduled === 1 ? 'clase programada' : 'clases programadas'} este mes</span>
              <span className={styles.muted}>Click en un día para ver detalles y cancelar si hace falta.</span>
            </div>

            <Calendar
              month={month}
              decorators={decorators}
              onDayClick={(d) => setPickedDay(d)}
              canGoPrev={false}
              canGoNext={false}
            />

            <div className={styles.legend}>
              <span className={styles.legendItem}>
                <span className={`${styles.dot} ${styles.dotIndividual}`} /> Individual
              </span>
              <span className={styles.legendItem}>
                <span className={`${styles.dot} ${styles.dotGroup}`} /> Grupal
              </span>
            </div>

            {error && <p className={styles.error}>{error}</p>}
          </>
        )}
      </main>

      <CreateGroupDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        teacherId={teacherId}
        onCreated={() => { setDrawerOpen(false); void reload(); }}
      />

      <TeacherDayDialog
        open={pickedDay !== null}
        onOpenChange={(o) => !o && setPickedDay(null)}
        day={pickedDay}
        bookings={dayBookings}
        onChanged={() => void reload()}
      />
    </>
  );
}
